<template>
  <modal
    v-if="open && order"
    :title="$t('Inventory.billing_done_title')"
    closable
    @close-request="requestClose"
  >
    <form class="billing-done-form" @submit.prevent="submit">
      <div class="billing-done-form__icon">
        <i class="fa fa-check-circle" aria-hidden="true" />
      </div>
      <h4>{{ $t('Inventory.billing_done_confirm') }}</h4>
      <p>{{ $t('Inventory.billing_done_help') }}</p>

      <div class="billing-done-form__order">
        <div>
          <span>{{ $t('Inventory.order') }}</span>
          <strong>{{ orderId }}</strong>
        </div>
        <div>
          <span>{{ $t('Inventory.vehicle_details') }}</span>
          <strong>{{ vehicleTitle }}</strong>
        </div>
        <div>
          <span>{{ $t('Inventory.vin') }}</span>
          <strong class="billing-done-form__vin">{{ order.vin || $t('Inventory.not_provided') }}</strong>
        </div>
      </div>

      <label class="form-group">
        <span>{{ $t('Inventory.external_billing_reference') }}</span>
        <input
          v-model.trim="billingReference"
          class="form-control"
          type="text"
          :placeholder="$t('Inventory.external_billing_reference_placeholder')"
        >
        <small>{{ $t('Inventory.optional') }}</small>
      </label>

      <div v-if="feedback" class="billing-done-form__error" role="alert">
        {{ feedback }}
      </div>

      <footer>
        <button type="button" class="btn btn-light" :disabled="submitting" @click="requestClose">
          {{ $t('Inventory.cancel') }}
        </button>
        <button type="submit" class="btn btn-kne-orange" :disabled="!canSubmit || submitting">
          <i :class="['fa', submitting ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
          {{ submitting ? $t('Inventory.marking_done') : $t('Inventory.confirm_billed_done') }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import { canBillInventory } from '../../../../inventory/inventorySelectors'
import { markInventoryBilled } from '../../../../inventory/inventoryService'

export default {
  name: 'InventoryBillingDoneModal',

  components: {
    Modal
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      billingReference: '',
      feedback: '',
      submitting: false
    }
  },

  computed: {
    companyUID () {
      return this.$store.state.authorization && this.$store.state.authorization.company
    },
    orderId () {
      if (!this.order) return ''
      return this.order['.key'] || this.order.orderNumber || this.order.key || ''
    },
    vehicleTitle () {
      if (!this.order) return ''
      return [this.order.year, this.order.make, this.order.model, this.order.trim]
        .filter(Boolean)
        .join(' ') || this.$t('Inventory.vehicle_pending')
    },
    operator () {
      const user = this.$store.state.user || {}
      const userInfo = this.$store.state.userInfo || {}
      return {
        uid: user.uid || '',
        name: userInfo.name || user.displayName || user.email || ''
      }
    },
    canSubmit () {
      return Boolean(this.companyUID && this.orderId && canBillInventory(this.order))
    }
  },

  watch: {
    open (value) {
      if (value) {
        this.billingReference = ''
        this.feedback = ''
        this.submitting = false
      }
    }
  },

  methods: {
    async submit () {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.feedback = ''
      try {
        const result = await markInventoryBilled({
          companyUID: this.companyUID,
          orderId: this.orderId,
          billedAt: Date.now(),
          operator: this.operator,
          billingReference: this.billingReference
        })
        this.$emit('completed', result)
        this.$emit('close')
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.billing_done_error')
      } finally {
        this.submitting = false
      }
    },
    requestClose () {
      if (!this.submitting) this.$emit('close')
    }
  }
}
</script>

<style scoped>
.billing-done-form {
  width: min(540px, 70vw);
  max-width: 100%;
  padding: 24px;
  color: #344250;
  text-align: center;
}

.billing-done-form__icon {
  margin-bottom: 12px;
  color: #43a96b;
  font-size: 3.4rem;
  line-height: 1;
}

.billing-done-form h4 {
  margin-bottom: 8px;
  color: #283644;
}

.billing-done-form > p {
  max-width: 430px;
  margin: 0 auto 18px;
  color: #6e7c89;
  font-size: .9rem;
}

.billing-done-form__order {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 14px;
  margin-bottom: 18px;
  border-radius: 10px;
  background: #f4f6f8;
  text-align: left;
}

.billing-done-form__order div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.billing-done-form__order span,
.billing-done-form label > span {
  margin-bottom: 3px;
  color: #7b8793;
  font-size: .73rem;
  font-weight: 700;
}

.billing-done-form__order strong {
  overflow: hidden;
  color: #334151;
  font-size: .87rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.billing-done-form__vin {
  font-family: monospace;
}

.billing-done-form label {
  text-align: left;
}

.billing-done-form label > span {
  display: block;
}

.billing-done-form label small {
  color: #87919c;
}

.billing-done-form__error {
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  color: #8d2f32;
  background: #fde7e7;
  text-align: left;
}

.billing-done-form footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e6eaee;
}

@media (max-width: 600px) {
  .billing-done-form {
    width: 100%;
    padding: 18px;
  }

  .billing-done-form__order {
    grid-template-columns: 1fr;
  }

  .billing-done-form footer {
    flex-direction: column-reverse;
  }

  .billing-done-form footer .btn {
    width: 100%;
  }
}
</style>
